import { useMutation } from '@apollo/client'
import {
  FileUpload,
  useFileUpload,
  useSnackbar
} from '@tecsinapse/cortex-react'
import { GetItemsPorPastaQueryName } from '../data/items-pasta-query'
import { UPLOAD_ARQUIVOS } from '../data/upload-arquivos'
import { UltimosModificadosQueryName } from '../data/ultimo-modificados-query'
import { t } from '@lingui/core/macro'
import {
  ItemBiblioteca,
  UploadArquivosMutationMutation,
  UploadArquivosMutationMutationVariables
} from '@/core/types/graphql'
import { Metadata } from '@/modules/biblioteca-virtual/types/types'
import { upload } from '@/modules/biblioteca-virtual/utils/functions'
import { maxSize } from '../utils/constants'
import { usePortalAxios } from '@tecsinapse-org/portal-menu-kit'
import { useState } from 'react'

const snackDuration = 10000

export const useUploaderDialog = ({
  pastaAtual,
  isFolder
}: {
  pastaAtual: ItemBiblioteca
  isFolder: boolean
}) => {
  const [hasError, setHasError] = useState(false)

  const [uploadArquivosMutation] = useMutation<
    UploadArquivosMutationMutation,
    UploadArquivosMutationMutationVariables
  >(UPLOAD_ARQUIVOS, {
    refetchQueries: [UltimosModificadosQueryName, GetItemsPorPastaQueryName]
  })
  const { instance } = usePortalAxios()
  const { snackbar } = useSnackbar()

  const onAccept = async (_accepted: FileUpload<Metadata>[]) => {
    const idPastaPai = pastaAtual.id

    const promises = (_accepted ?? []).map(async file => {
      try {
        const r = await upload(instance, file)
        return {
          ...file,
          status: 'success',
          metadata: {
            id: String(r.id)
          }
        } as FileUpload<Metadata>
      } catch (e) {
        snackbar.show('error', (e as Error).message, {
          duration: snackDuration
        })
        setHasError(true)
        throw new Error((e as Error).message)
      }
    })

    const files = await Promise.all(promises)

    if (files) {
      try {
        await uploadArquivosMutation({
          variables: {
            arquivos: files
              .filter(it => it.status === 'success')
              .map(file => {
                return {
                  idArquivo: String(file.metadata?.id),
                  nome: file.file.name,
                  relativePath: file.file.relativePath,
                  idPastaPai
                }
              })
          }
        })

        snackbar.show('success', t`Arquivo(s) carregados com sucesso!`, {
          duration: snackDuration
        })

        setHasError(false)
        onClose()
      } catch (error) {
        setHasError(true)
        console.error(error)
        snackbar.show('error', (error as Error).message, {
          duration: snackDuration
        })
        throw new Error((error as Error).message)
      }
    }

    return files
  }

  const { dropzoneProps, onClose, onOpen, open } = useFileUpload({
    allowMultiple: true,
    onAccept,
    maxSize,
    preventDuplicates: true,
    onDuplicate: () => {
      snackbar.show('warning', t`Arquivo duplicado. Não foi possível enviar.`, {
        duration: snackDuration
      })
    },
    isFolder,
    onOpenManager: () => {
      snackbar.show(
        'warning',
        t`Não feche nem recarregue a aba do navegador durante o upload`,
        { duration: snackDuration }
      )
    },
    onFileRejected: rejections => {
      rejections.forEach(({ file, errors }) => {
        if (file.size > maxSize) {
          snackbar.show('error', t`Arquivo maior que o limite`, {
            duration: snackDuration
          })
        } else {
          errors.forEach(error =>
            snackbar.show('error', error.message, { duration: snackDuration })
          )
        }
      })
    },
    uploadProgressText: t`Upload(s) em progresso`,
    uploadResultText: hasError
      ? t`O upload falhou, verifique os arquivos`
      : t`Upload(s) concluído(s) com sucesso!`,
    noClick: isFolder
  })

  return {
    open,
    onOpen,
    onClose,
    dropzoneProps
  }
}
